import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useImpersonation } from '../contexts/ImpersonationContext';
import { 
  getComptes, 
  createCompte, 
  updateCompte, 
  deleteCompte,
  Compte 
} from '../lib/services/AccountService';

interface UseAccountsReturn {
  comptes: Compte[];
  loading: boolean;
  error: string | null;
  refreshComptes: () => Promise<void>;
  addCompte: (compte: Compte) => Promise<{ success: boolean; error?: string; data?: Compte }>;
  editCompte: (id: string, updates: Partial<Compte>) => Promise<{ success: boolean; error?: string; data?: Compte }>;
  removeCompte: (id: string) => Promise<{ success: boolean; error?: string }>;
}

export const useAccounts = (): UseAccountsReturn => {
  const { user } = useAuth();
  const { impersonatedUserId } = useImpersonation();
  const [comptes, setComptes] = useState<Compte[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchComptes = async () => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('useAccounts - Récupération des comptes...');
      
      const { data, error: fetchError } = await getComptes();
      
      if (fetchError) {
        console.error('useAccounts - Erreur lors de la récupération:', fetchError);
        setError(fetchError.message || 'Erreur lors de la récupération des comptes');
        return;
      }

      if (data) {
        console.log('useAccounts - Comptes récupérés:', data.length);
        setComptes(data);
      } else {
        console.log('useAccounts - Aucun compte trouvé');
        setComptes([]);
      }
    } catch (err) {
      console.error('useAccounts - Exception:', err);
      setError(err instanceof Error ? err.message : 'Erreur inattendue');
    } finally {
      setLoading(false);
    }
  };

  const refreshComptes = async () => {
    await fetchComptes();
  };

  const addCompte = async (compte: Compte): Promise<{ success: boolean; error?: string; data?: Compte }> => {
    try {
      console.log('useAccounts - Ajout d\'un compte:', compte);
      
      const { data, error: createError } = await createCompte(compte);
      
      if (createError) {
        console.error('useAccounts - Erreur lors de la création:', createError);
        return { success: false, error: createError.message || 'Erreur lors de la création' };
      }

      if (data) {
        console.log('useAccounts - Compte créé avec succès');
        await fetchComptes();
        return { success: true, data };
      }

      return { success: false, error: 'Erreur lors de la création' };
    } catch (err) {
      console.error('useAccounts - Exception lors de la création:', err);
      return { success: false, error: err instanceof Error ? err.message : 'Erreur inattendue' };
    }
  };

  const editCompte = async (id: string, updates: Partial<Compte>): Promise<{ success: boolean; error?: string; data?: Compte }> => {
    try {
      console.log('useAccounts - Modification du compte:', id, updates);
      
      const { data, error: updateError } = await updateCompte(id, updates);
      
      if (updateError) {
        console.error('useAccounts - Erreur lors de la modification:', updateError);
        return { success: false, error: updateError.message || 'Erreur lors de la modification' };
      }

      if (data) {
        console.log('useAccounts - Compte modifié avec succès');
        await fetchComptes();
        return { success: true, data };
      }

      return { success: false, error: 'Erreur lors de la modification' };
    } catch (err) {
      console.error('useAccounts - Exception lors de la modification:', err);
      return { success: false, error: err instanceof Error ? err.message : 'Erreur inattendue' };
    }
  };

  const removeCompte = async (id: string): Promise<{ success: boolean; error?: string }> => {
    try {
      console.log('useAccounts - Suppression du compte:', id);
      
      const { success, error: deleteError } = await deleteCompte(id);
      
      if (!success) {
        console.error('useAccounts - Erreur lors de la suppression:', deleteError);
        return { success: false, error: deleteError?.message || 'Erreur lors de la suppression' };
      }

      console.log('useAccounts - Compte supprimé avec succès');
      await fetchComptes();
      return { success: true };
    } catch (err) {
      console.error('useAccounts - Exception lors de la suppression:', err);
      return { success: false, error: err instanceof Error ? err.message : 'Erreur inattendue' };
    }
  };

  useEffect(() => {
    if (user) {
      fetchComptes();
    } else {
      setComptes([]);
      setLoading(false);
    }
  }, [user, impersonatedUserId]);

  useEffect(() => {
    const onComptesChanged = () => {
      if (user) fetchComptes();
    };
    const onImpersonationChanged = () => {
      if (user) fetchComptes();
    };
    window.addEventListener('suivifinance:comptes-changed', onComptesChanged);
    window.addEventListener('suivifinance:impersonation-changed', onImpersonationChanged);
    return () => {
      window.removeEventListener('suivifinance:comptes-changed', onComptesChanged);
      window.removeEventListener('suivifinance:impersonation-changed', onImpersonationChanged);
    };
  }, [user]);

  return {
    comptes,
    loading,
    error,
    refreshComptes,
    addCompte,
    editCompte,
    removeCompte
  };
}; 